import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Dialog,
  Stack,
  TextField,
  Typography,
  DialogActions,
  DialogTitle,
  CircularProgress,
  Paper,
  Alert,
  Tooltip,
  ListItemProps,
  AccordionProps
} from '@mui/material'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import { LoadingButton } from '@mui/lab'
import { Delete, Edit, ExpandMore, Upload } from '@mui/icons-material'
import { Asset, AssetInput } from '../types'
import { useTranslation } from 'react-i18next'
import { ChangeEvent, ReactNode, useState } from 'react'
import { useAccordion, useDialog, usePolicy } from '../hooks'
import { deleteAssetPhoto, updateAsset, uploadAssetPhoto } from '../services/assetService'
import { styled } from '@mui/material/styles'
import {
  createErrorNotification,
  createErrorOrSuccessNotification,
  createSuccessNotification
} from '../redux/slices/notifications'
import { NotificationType } from '../enums/NotificationType'
import { useDispatch } from 'react-redux'
import { Form, Formik, FormikHelpers } from 'formik'
import { humanizeTimestamp } from '../utils/date'
import { isAfter, isBefore } from 'date-fns'
import useAssetTimestamps from '../hooks/useAssetTimestamps'
import AssetDetailsList from './AssetDetailsList'

const HiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
})

interface EditableListItem extends ListItemProps {
  editable?: boolean
}
interface AssetDetailsAttachedListItemProps extends Omit<EditableListItem, 'children'> {
  asset: Asset
  beforeSubmit?: (values: AssetInput, helpers: FormikHelpers<AssetInput>) => void
  afterSubmit?: (asset: Asset, helpers: FormikHelpers<AssetInput>) => void
}

function AssetDetailsAttachedListItem({
  asset,
  editable = true,
  beforeSubmit,
  afterSubmit,
  ...props
}: AssetDetailsAttachedListItemProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy context.
   */
  const { isSuperadmin } = usePolicy()

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The attachment timestamps.
   */
  const { attachedAt } = useAssetTimestamps(asset)

  /**
   * The intial values.
   */
  const initialValues: AssetInput = {
    name: asset.name,
    company: asset.companyId,
    project: asset.projectId,
    level: asset.levelId,
    lift: asset.liftId,
    attachedAt
  }

  async function handleSubmit(
    values: AssetInput,
    { setSubmitting, ...helpers }: FormikHelpers<AssetInput>
  ) {
    try {
      setSubmitting(true)

      beforeSubmit?.(values, { setSubmitting, ...helpers })

      const updatedAsset = await updateAsset(asset.id, values)
      dispatch(createSuccessNotification(t('asset:assetUpdateSuccess')))

      setSubmitting(false)
      afterSubmit?.(updatedAsset, { setSubmitting, ...helpers })
      closeDialog()
    } catch (_error) {
      dispatch(createErrorNotification(t('asset:assetUpdateFailed')))
      setSubmitting(false)
    }
  }

  return (
    <>
      {isSuperadmin && (
        <>
          <ListItem key="attached" disableGutters disablePadding {...props}>
            <ListItemButton>
              <ListItemText
                primary={
                  asset?.attachedAt
                    ? humanizeTimestamp(asset?.attachedAt)
                    : humanizeTimestamp(attachedAt)
                }
                secondary={t('asset:attachedAt')}
              />
              {editable && (
                <ListItemSecondaryAction>
                  <Tooltip title={t('edit') as string}>
                    <IconButton onClick={openDialog}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </ListItemButton>
          </ListItem>

          <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
            <DialogTitle>{t('asset:selectAttachedAt')}</DialogTitle>
            <Formik initialValues={initialValues} onSubmit={handleSubmit}>
              {({ values, isSubmitting, setFieldValue, handleSubmit }) => (
                <Form onSubmit={handleSubmit}>
                  <DialogContent>
                    <Stack direction="row" alignItems="center" gap={3}>
                      <DatePicker
                        label={t('date')}
                        value={values?.attachedAt ?? null}
                        onChange={(value) => {
                          if (
                            value === null ||
                            asset?.detachedAt === undefined ||
                            asset?.detachedAt === null ||
                            isBefore(value, new Date(asset.detachedAt))
                          ) {
                            setFieldValue('attachedAt', value)
                          }
                        }}
                      />
                      <TimePicker
                        label={t('time')}
                        value={values?.attachedAt ?? null}
                        sx={{ width: '100px' }}
                        onChange={(value) => {
                          if (
                            value === null ||
                            asset?.detachedAt === undefined ||
                            asset?.detachedAt === null ||
                            isBefore(value, new Date(asset.detachedAt))
                          ) {
                            setFieldValue('attachedAt', value)
                          }
                        }}
                        // @ts-expect-error typed incorrectly
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button variant="text" color="inherit" onClick={closeDialog}>
                      {t('cancel')}
                    </Button>

                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      loading={isSubmitting}
                    >
                      {t('save')}
                    </LoadingButton>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </Dialog>
        </>
      )}
    </>
  )
}

interface AssetDetailsDetachedListItemProps extends Omit<EditableListItem, 'children'> {
  asset: Asset
  beforeSubmit?: (values: AssetInput, helpers: FormikHelpers<AssetInput>) => void
  afterSubmit?: (asset: Asset, helpers: FormikHelpers<AssetInput>) => void
}

function AssetDetailsDetachedListItem({
  asset,
  editable = true,
  beforeSubmit,
  afterSubmit,
  ...props
}: AssetDetailsDetachedListItemProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy context.
   */
  const { isSuperadmin } = usePolicy()

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The detachedAt timestamp.
   */
  const { detachedAt } = useAssetTimestamps(asset)

  /**
   * The intial values.
   */
  const initialValues: AssetInput = {
    name: asset.name,
    company: asset.companyId,
    project: asset.projectId,
    level: asset.levelId,
    lift: asset.liftId,
    detachedAt
  }

  async function handleSubmit(
    values: AssetInput,
    { setSubmitting, ...helpers }: FormikHelpers<AssetInput>
  ) {
    try {
      setSubmitting(true)

      beforeSubmit?.(values, { setSubmitting, ...helpers })

      const updatedAsset = await updateAsset(asset.id, values)
      dispatch(createSuccessNotification(t('asset:assetUpdateSuccess')))

      setSubmitting(false)
      afterSubmit?.(updatedAsset, { setSubmitting, ...helpers })
      closeDialog()
    } catch (_error) {
      dispatch(createErrorNotification(t('asset:assetUpdateFailed')))
      setSubmitting(false)
    }
  }

  return (
    <>
      {isSuperadmin && detachedAt !== null && (
        <>
          <ListItem disableGutters disablePadding {...props}>
            <ListItemButton>
              <ListItemText
                primary={
                  asset?.detachedAt
                    ? humanizeTimestamp(asset.detachedAt)
                    : detachedAt
                      ? humanizeTimestamp(detachedAt)
                      : '-'
                }
                secondary={t('asset:detachedAt')}
              />

              {editable && (
                <ListItemSecondaryAction>
                  <Tooltip title={t('edit') as string}>
                    <IconButton onClick={openDialog}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </ListItemButton>
          </ListItem>

          <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
            <DialogTitle>{t('asset:selectDetachedAt')}</DialogTitle>
            <Formik initialValues={initialValues} onSubmit={handleSubmit}>
              {({ values, isSubmitting, setFieldValue, handleSubmit }) => (
                <Form onSubmit={handleSubmit}>
                  <DialogContent>
                    <Stack direction="row" alignItems="center" gap={3}>
                      <DatePicker
                        label={t('date')}
                        value={values?.detachedAt ?? null}
                        onChange={(value: Date | null) => {
                          if (
                            value === null ||
                            (asset?.attachedAt !== undefined &&
                              asset.attachedAt !== null &&
                              isAfter(value, new Date(asset.attachedAt)))
                          ) {
                            setFieldValue('detachedAt', value)
                          }
                        }}
                      />

                      <TimePicker
                        label={t('time')}
                        value={values?.detachedAt ?? null}
                        sx={{ width: '100px' }}
                        onChange={(value) => {
                          if (
                            value === null ||
                            (asset?.attachedAt !== undefined &&
                              asset.attachedAt !== null &&
                              isAfter(value, new Date(asset.attachedAt)))
                          ) {
                            setFieldValue('detachedAt', value)
                          }
                        }}
                        // @ts-expect-error typed incorrectly
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button variant="text" color="inherit" onClick={closeDialog}>
                      {t('cancel')}
                    </Button>

                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      loading={isSubmitting}
                    >
                      {t('save')}
                    </LoadingButton>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </Dialog>
        </>
      )}
    </>
  )
}

interface AssetDetailsProps extends Omit<AccordionProps, 'expanded' | 'onChange' | 'children'> {
  children?: ReactNode
  asset: Asset | null
  editable?: boolean
  onUpdate?: (asset: Asset) => void
}

export default function AssetDetails({
  asset,
  editable = true,
  onUpdate,
  children,
  defaultExpanded,
  ...props
}: AssetDetailsProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy context.
   */
  const { canEditAsset } = usePolicy()

  /**
   * The uploaded file.
   */
  const [file, setFile] = useState<File | null>(null)

  /**
   * Indicates if the component photo is being uploaded.
   */
  const [isUploadingPhoto, setIsUploadingPhoto] = useState<boolean>(false)

  /**
   * Indicates if the photo is being deleted.
   */
  const [isDeletingPhoto, setIsDeletingPhoto] = useState<boolean>(false)

  /**
   * Indicates if the photo dialog is open.
   */
  const [isPhotoDialogOpen, setIsPhotoDialogOpen] = useState<boolean>(false)

  /**
   * The URL of the uploaded file.
   */
  const fileUrl: string | null = file !== null ? URL.createObjectURL(file) : null

  /**
   * The accordion state.
   */
  const { expanded, toggleAccordion } = useAccordion(defaultExpanded)

  function openPhotoDialog(): void {
    setIsPhotoDialogOpen(true)
  }

  function closePhotoDialog(): void {
    setIsPhotoDialogOpen(false)
    setFile(null)
  }

  function dispatchError(message: string) {
    dispatch(createErrorOrSuccessNotification(NotificationType.WARNING, message))
  }

  function handlePhotoUpload(event: ChangeEvent): void {
    const target = event.target as HTMLInputElement
    const newFile = target.files?.[0]

    if (newFile && newFile.type.startsWith('image/')) {
      setFile(newFile)
      openPhotoDialog()
    }
  }

  /**
   * Upload photo.
   */
  async function uploadPhoto(): Promise<void> {
    try {
      setIsUploadingPhoto(true)

      if (asset !== null && file !== null) {
        onUpdate?.(await uploadAssetPhoto(asset.id, file))
      }

      setIsUploadingPhoto(false)
      closePhotoDialog()
    } catch (_error) {
      dispatchError('Failed to upload photo!')
      setIsUploadingPhoto(false)
    }
  }

  /**
   * Delete the asset photo.
   */
  async function deletePhoto(): Promise<void> {
    try {
      setIsDeletingPhoto(true)

      if (asset !== null) {
        onUpdate?.(await deleteAssetPhoto(asset.id))
      }

      setIsDeletingPhoto(false)
    } catch (_error) {
      dispatchError('Failed to delete asset photo!')
      setIsDeletingPhoto(false)
    }
  }

  return (
    <Accordion expanded={expanded} onChange={toggleAccordion} {...props}>
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ pr: 6 }}>
        <Box px={3}>
          <Typography variant="h6">{t('assetInformation')}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {asset ? (
          <>
            <AssetDetailsList
              asset={asset}
              editable={editable}
              disablePadding
              afterUpdate={onUpdate}
            />
            <List>
              {asset?.photo ? (
                <ListItem key="photo" disableGutters disablePadding>
                  <ListItemButton>
                    {asset?.photoUrl ? (
                      <Paper elevation={6}>
                        <img
                          src={asset.photoUrl}
                          style={{
                            display: 'block',
                            objectFit: 'cover',
                            width: '64px',
                            height: '64px',
                            borderRadius: '4px'
                          }}
                        />
                      </Paper>
                    ) : (
                      <CircularProgress />
                    )}

                    {canEditAsset(asset) && (
                      <ListItemSecondaryAction>
                        <Stack direction="row" gap={1}>
                          <Tooltip title={t('deletePhoto') as string}>
                            <LoadingButton
                              component={IconButton}
                              color="error"
                              size="small"
                              disabled={isDeletingPhoto}
                              loading={isDeletingPhoto}
                              onClick={deletePhoto}
                              sx={{ borderRadius: '50%', p: 2, minWidth: 0 }}
                            >
                              <Delete />
                            </LoadingButton>
                          </Tooltip>

                          <Tooltip title={t('uploadNewPhoto') as string}>
                            <Button
                              component="label"
                              variant="text"
                              size="small"
                              tabIndex={-1}
                              sx={{ borderRadius: '50%', p: 2, minWidth: 0 }}
                            >
                              <Upload />
                              <HiddenInput type="file" onChange={handlePhotoUpload} />
                            </Button>
                          </Tooltip>
                        </Stack>
                      </ListItemSecondaryAction>
                    )}
                  </ListItemButton>
                </ListItem>
              ) : (
                <>
                  {canEditAsset(asset) && (
                    <ListItem key="photo" sx={{ py: 4 }}>
                      <Button component="label" variant="outlined" size="small" tabIndex={-1}>
                        {t('addPhoto')}
                        <HiddenInput type="file" onChange={handlePhotoUpload} />
                      </Button>
                    </ListItem>
                  )}
                </>
              )}

              <Dialog open={isPhotoDialogOpen} onClose={closePhotoDialog} fullWidth maxWidth="sm">
                <DialogTitle>{t('previewPhoto')}</DialogTitle>
                <DialogContent>
                  {fileUrl !== null && (
                    <Paper elevation={6}>
                      <img
                        src={fileUrl}
                        style={{
                          display: 'block',
                          objectFit: 'cover',
                          width: '552px',
                          height: '552px',
                          borderRadius: '4px'
                        }}
                      />
                    </Paper>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button disabled={isUploadingPhoto} onClick={closePhotoDialog}>
                    {t('cancel')}
                  </Button>

                  <LoadingButton
                    variant="contained"
                    disabled={isUploadingPhoto}
                    loading={isUploadingPhoto}
                    onClick={uploadPhoto}
                  >
                    {t('save')}
                  </LoadingButton>
                </DialogActions>
              </Dialog>

              <AssetDetailsAttachedListItem
                key="attached"
                asset={asset}
                editable={editable}
                afterSubmit={onUpdate}
              />

              <AssetDetailsDetachedListItem
                key="detached"
                asset={asset}
                editable={editable}
                afterSubmit={onUpdate}
              />
            </List>
          </>
        ) : (
          <Alert variant="outlined" severity="error" sx={{ border: 0 }}>
            {t('assetNotFound')}
          </Alert>
        )}

        {children}
      </AccordionDetails>
    </Accordion>
  )
}
